import React from 'react';
import Box from '@mui/material/Box';
import {
  IRapportConditionControl,
  IRapportMallControl,
} from './RapportMallTypes';
import {
  HOVER_SHADOW,
  SELECTED_SHADOW,
  getPositionStyle,
  getZIndexesForControls,
} from './RapportMallUtils';
import { RapportKontroll } from './RapportKontroll';
import { RapportKontrollEditPosition } from './RapportKontrollEditPosition';
import { RapportFontProvider } from './RapportFontContext';
import { RapportKontrollHeader } from './RapportKontrollHeader';
import TextField from '@mui/material/TextField';

export interface IProps {
  ctrl: IRapportConditionControl;
  sx?: any;
  onSelect?: (path: number[]) => void;
  onUpdate?: (
    controlPath: number[],
    updatedControl: IRapportMallControl
  ) => void;
  controlPath: number[];
  selectedPath?: number[];
  selected?: boolean;
  maxWidth: number;
  defaultHeight: number;
  zIndex?: number; // Används för att hantera z-index i behållare
  showDottedBorders: boolean;
}

export const RapportCondition: React.FC<IProps> = props => {
  const {
    ctrl,
    onSelect,
    onUpdate,
    controlPath,
    selectedPath,
    selected,
    maxWidth,
    defaultHeight,
  } = props;
  const [childHovering, setChildHovering] = React.useState(false);
  const [containerHovering, setContainerHovering] = React.useState(false);
  const content = ctrl.Content || [];
  const zIndexes = getZIndexesForControls(content, 1);

  const handleSelect = () => {
    if (onSelect) {
      onSelect(controlPath);
    }
  };

  const ctrlWidth = ctrl.Width ?? maxWidth;
  const ctrlHeight = ctrl.Height ?? defaultHeight;

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        position: 'relative',
        ...getPositionStyle(
          ctrl,
          props.controlPath.length > 1,
          ctrlWidth,
          ctrlHeight
        ),
        transition: 'box-shadow 0.2s, border-color 0.2s',
        boxShadow: selected
          ? SELECTED_SHADOW
          : containerHovering && !childHovering
          ? HOVER_SHADOW
          : undefined,
        zIndex: props.zIndex,
      }}
      onMouseEnter={() => setContainerHovering(true)}
      onMouseLeave={() => setContainerHovering(false)}
      onClick={e => {
        e.stopPropagation();
        handleSelect();
      }}
      data-control-path={props.controlPath.join('-')}
    >
      <RapportFontProvider defaultFont={ctrl.DefaultFont}>
        {content.map((child: IRapportMallControl, i: number) => (
          <RapportKontroll
            ctrl={child}
            idx={i}
            key={i}
            maxWidth={ctrlWidth}
            defaultHeight={ctrlHeight}
            setChildHovering={setChildHovering}
            onSelect={onSelect}
            onUpdate={onUpdate}
            controlPath={[...controlPath, i]}
            selectedPath={selectedPath}
            zIndex={zIndexes[i]}
            showDottedBorders={props.showDottedBorders}
          />
        ))}
      </RapportFontProvider>
    </Box>
  );
};

export interface IRapportConditionEditProps {
  value: IRapportConditionControl;
  onChange: (ctrl: IRapportConditionControl) => void;
}

export const RapportConditionEdit: React.FC<
  IRapportConditionEditProps
> = props => (
  <Box sx={{ mt: 2 }}>
    <RapportKontrollHeader value={props.value} />
    <TextField
      label="Villkor"
      value={props.value.Condition}
      onChange={e =>
        props.onChange({ ...props.value, Condition: e.target.value })
      }
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
    <TextField
      label="Villkorsvärde"
      value={props.value.Value}
      onChange={e => props.onChange({ ...props.value, Value: e.target.value })}
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
    <TextField
      label="Förhandsgranskning, villkor"
      value={props.value.PreviewCondition}
      onChange={e =>
        props.onChange({ ...props.value, PreviewCondition: e.target.value })
      }
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
    <TextField
      label="Förhandsgranskning, villkorsvärde"
      value={props.value.PreviewValue}
      onChange={e =>
        props.onChange({ ...props.value, PreviewValue: e.target.value })
      }
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
    <TextField
      label="Font"
      value={props.value.DefaultFont}
      onChange={e =>
        props.onChange({ ...props.value, DefaultFont: e.target.value })
      }
      fullWidth
      size="small"
      sx={{ mb: 2 }}
    />
    <RapportKontrollEditPosition
      value={props.value}
      onChange={props.onChange}
    />
  </Box>
);
